import React, { useEffect, useRef } from 'react';

const nodes = [
  [0.08, 0.18], [0.24, 0.09], [0.42, 0.21], [0.61, 0.11], [0.83, 0.2],
  [0.16, 0.43], [0.35, 0.39], [0.53, 0.5], [0.72, 0.4], [0.92, 0.48],
  [0.07, 0.71], [0.28, 0.63], [0.46, 0.78], [0.68, 0.69], [0.87, 0.8]
];

const links = [
  [0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [1, 6], [2, 6], [2, 7],
  [3, 8], [4, 9], [5, 6], [6, 7], [7, 8], [8, 9], [5, 10], [6, 11],
  [7, 12], [8, 13], [9, 14], [10, 11], [11, 12], [12, 13], [13, 14]
];

const renderGrid = (context, width, height) => {
  context.strokeStyle = 'rgba(255, 255, 255, 0.045)';
  context.lineWidth = 1;
  for (let x = 0; x <= width; x += 72) {
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, height);
    context.stroke();
  }
  for (let y = 0; y <= height; y += 72) {
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
  }
};

const renderLinks = (context, points, phase) => {
  links.forEach(([from, to], index) => {
    const start = points[from];
    const end = points[to];
    context.strokeStyle = 'rgba(125, 211, 252, 0.24)';
    context.beginPath();
    context.moveTo(start.x, start.y);
    context.lineTo(end.x, end.y);
    context.stroke();

    const progress = (phase * 0.00008 + index * 0.137) % 1;
    const x = start.x + (end.x - start.x) * progress;
    const y = start.y + (end.y - start.y) * progress;
    context.fillStyle = index % 3 === 0 ? '#a3e635' : '#7dd3fc';
    context.fillRect(x - 2, y - 2, 4, 4);
  });
};

const renderNodes = (context, points) => {
  points.forEach((point, index) => {
    const size = index === 7 ? 13 : 8;
    context.fillStyle = '#050505';
    context.strokeStyle = index === 7 ? '#a3e635' : 'rgba(255, 255, 255, 0.72)';
    context.lineWidth = index === 7 ? 2 : 1;
    context.fillRect(point.x - size / 2, point.y - size / 2, size, size);
    context.strokeRect(point.x - size / 2, point.y - size / 2, size, size);
  });
};

const renderTopology = (context, width, height, phase, pointer) => {
  context.clearRect(0, 0, width, height);
  renderGrid(context, width, height);
  const points = nodes.map(([x, y]) => ({
    x: x * width + pointer.x * (x - 0.5) * 14,
    y: y * height + pointer.y * (y - 0.5) * 14
  }));
  renderLinks(context, points, phase);
  renderNodes(context, points);
};

const TechnologyCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const pointer = { x: 0, y: 0 };
    let width = 0;
    let height = 0;
    let animationFrame;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      width = bounds.width;
      height = bounds.height;
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      context.setTransform(scale, 0, 0, scale, 0, 0);
      if (reducedMotion) renderTopology(context, width, height, 0, pointer);
    };
    const movePointer = (event) => {
      pointer.x = event.clientX / window.innerWidth - 0.5;
      pointer.y = event.clientY / window.innerHeight - 0.5;
    };
    const animate = (time) => {
      renderTopology(context, width, height, time, pointer);
      animationFrame = window.requestAnimationFrame(animate);
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    if (!reducedMotion) window.addEventListener('pointermove', movePointer, { passive: true });
    resize();
    if (!reducedMotion) animationFrame = window.requestAnimationFrame(animate);
    return () => {
      observer.disconnect();
      window.removeEventListener('pointermove', movePointer);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden="true" />;
};

export default TechnologyCanvas;
